// A calm one-line banner when a new brief is ready: the one thing to look at first, then which
// brief it is ("Needs you now: Sign the lease. · Your 1pm brief"). It links to the brief and can
// be waved away; it never flashes or counts up.
import { Link } from "@tanstack/react-router";
import { Sunrise, X } from "lucide-react";
import { useBriefs } from "@web/lib/notifyQueries";
import { useNow } from "@web/lib/useNow";
import { notifyStore, useDismissedBriefs } from "./notifyStore";
import { bannerBrief } from "./wording";

export function BriefBanner({ pathname, enabled }: { pathname: string; enabled: boolean }) {
  const briefs = useBriefs(enabled);
  const now = useNow(60_000);
  const dismissed = useDismissedBriefs();
  const brief = bannerBrief(briefs.data, now, dismissed);
  if (!brief || pathname === "/brief" || pathname.startsWith("/brief/")) return null;
  return (
    <div
      role="status"
      aria-label="New brief"
      className="flex shrink-0 items-center gap-3 border-b border-line bg-paper px-4 py-2 text-sm"
    >
      <Sunrise aria-hidden className="size-4 shrink-0 text-accent-text" />
      <Link
        to="/brief/$briefId"
        params={{ briefId: brief.id }}
        className="min-w-0 flex-1 truncate text-ink hover:underline"
      >
        <span className="font-medium">{brief.headline}</span>
        <span className="text-muted"> · {brief.label}</span>
      </Link>
      <Link
        to="/brief/$briefId"
        params={{ briefId: brief.id }}
        className="hidden shrink-0 text-accent-text underline underline-offset-2 sm:inline"
      >
        Read it
      </Link>
      <button
        type="button"
        onClick={() => notifyStore.dismissBrief(brief.id)}
        aria-label="Not now"
        title="Not now"
        className="shrink-0 rounded-md p-1 text-muted hover:bg-sunk hover:text-ink"
      >
        <X aria-hidden className="size-4" />
      </button>
    </div>
  );
}
